import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilValue } from 'recoil';
import { cn } from '@/lib/utils';
import { ComponentType, visibleComponentsByZoneSelector, DropZone } from '@/state/dragDrop';
import { componentVisibilityState } from '@/state/layout';

interface ComponentBounds {
  type: ComponentType;
  zone: DropZone;
  element: HTMLElement;
  rect: DOMRect;
}

interface DropZoneInfo {
  targetZone: DropZone;
  label: string;
  description: string;
  bounds: DOMRect;
  isEdge?: boolean;
  isSwap?: boolean;
  swapWith?: ComponentType;
}

interface DynamicDropOverlayProps {
  isActive: boolean;
  draggedComponent?: ComponentType;
  containerRef: React.RefObject<HTMLDivElement>;
  onDrop?: (targetZone: DropZone, event: DragEvent) => void;
}

/**
 * Provides real-time visual feedback during drag operations by tracking actual
 * component positions and calculating drop zones dynamically.
 * Swap: hover anywhere over a target component.
 * Edge placement (2-component only): drag to the container edge.
 */
export const DynamicDropOverlay: React.FC<DynamicDropOverlayProps> = ({
  isActive,
  draggedComponent,
  containerRef,
  onDrop
}) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentDropZone, setCurrentDropZone] = useState<DropZoneInfo | null>(null);
  const [componentBounds, setComponentBounds] = useState<ComponentBounds[]>([]);

  const visibleComponentsByZone = useRecoilValue(visibleComponentsByZoneSelector);
  const visibility = useRecoilValue(componentVisibilityState);

  const updateComponentBounds = useCallback(() => {
    if (!containerRef.current || !isActive) return;

    const bounds: ComponentBounds[] = [];
    const container = containerRef.current;
    const draggableElements = container.querySelectorAll('[data-component-type]');

    draggableElements.forEach((element) => {
      const componentType = element.getAttribute('data-component-type') as ComponentType;
      const rect = element.getBoundingClientRect();

      if (componentType && componentType !== draggedComponent && visibility[componentType]) {
        const zone = Object.entries(visibleComponentsByZone).find(([_, components]) =>
          components.includes(componentType)
        )?.[0] as DropZone;

        if (zone) {
          bounds.push({ type: componentType, zone, element: element as HTMLElement, rect });
        }
      }
    });

    setComponentBounds(bounds);
  }, [containerRef.current, isActive, draggedComponent, visibility, visibleComponentsByZone]);

  useEffect(() => {
    if (isActive) {
      updateComponentBounds();
      const handleResize = () => updateComponentBounds();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, [isActive, updateComponentBounds]);

  useEffect(() => {
    if (!isActive) {
      setMousePosition({ x: 0, y: 0 });
      return;
    }

    let animationFrame: number | null = null;

    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
      if (animationFrame) return;
      animationFrame = requestAnimationFrame(() => {
        setMousePosition({ x: event.clientX, y: event.clientY });
        animationFrame = null;
      });
    };

    document.addEventListener('dragover', handleDragOver);
    return () => {
      document.removeEventListener('dragover', handleDragOver);
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [isActive]);

  const dropZoneInfo = useMemo((): DropZoneInfo | null => {
    if (!isActive || !containerRef.current || !draggedComponent || componentBounds.length === 0) {
      return null;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const relX = mousePosition.x - containerRect.left;
    const relY = mousePosition.y - containerRect.top;

    if (relX < 0 || relX > containerRect.width || relY < 0 || relY > containerRect.height) {
      return null;
    }

    const visibleCount = Object.values(visibleComponentsByZone).flat().length;

    if (visibleCount === 3 && componentBounds.length === 2) {
      const [a, b] = componentBounds;
      const overlapLeft = Math.max(a.rect.left, b.rect.left);
      const overlapRight = Math.min(a.rect.right, b.rect.right);
      const horizontalOverlap = overlapRight - overlapLeft;
      const minWidth = Math.min(a.rect.width, b.rect.width);

      if (horizontalOverlap > minWidth * 0.5) {
        const top = a.rect.top < b.rect.top ? a : b;
        const bottom = a.rect.top < b.rect.top ? b : a;
        const gapCenterY = (top.rect.bottom + bottom.rect.top) / 2;
        const gapThreshold = 20;
        const stackLeft = Math.min(a.rect.left, b.rect.left);
        const stackRight = Math.max(a.rect.right, b.rect.right);

        if (mousePosition.x >= stackLeft && mousePosition.x <= stackRight &&
            mousePosition.y >= gapCenterY - gapThreshold && mousePosition.y <= gapCenterY + gapThreshold) {
          const stackCenterX = (stackLeft + stackRight) / 2;
          const containerCenterX = containerRect.left + containerRect.width / 2;
          const targetSide = stackCenterX < containerCenterX ? 'left' : 'right';

          return {
            targetZone: targetSide as DropZone,
            label: `Move to ${targetSide === 'left' ? 'Left' : 'Right'} Side`,
            description: 'Swap sides with stacked components',
            bounds: new DOMRect(
              stackLeft,
              Math.min(a.rect.top, b.rect.top),
              stackRight - stackLeft,
              Math.max(a.rect.bottom, b.rect.bottom) - Math.min(a.rect.top, b.rect.top)
            ),
            isEdge: true
          };
        }
      }
    }

    for (const component of componentBounds) {
      const { rect } = component;
      if (mousePosition.x >= rect.left && mousePosition.x <= rect.right &&
          mousePosition.y >= rect.top && mousePosition.y <= rect.bottom) {
        return {
          targetZone: component.zone,
          label: `Swap with ${component.type}`,
          description: `Exchange positions with ${component.type}`,
          bounds: rect,
          isSwap: true,
          swapWith: component.type
        };
      }
    }
    const edgeThreshold = 60;

    if (visibleCount === 2) {
      if (relX <= edgeThreshold) {
        return {
          targetZone: 'left' as DropZone,
          label: 'Place Left',
          description: 'Move component to the left side',
          bounds: new DOMRect(containerRect.left, containerRect.top, containerRect.width / 2, containerRect.height),
          isEdge: true
        };
      }
      if (relX >= containerRect.width - edgeThreshold) {
        return {
          targetZone: 'right' as DropZone,
          label: 'Place Right',
          description: 'Move component to the right side',
          bounds: new DOMRect(containerRect.left + containerRect.width / 2, containerRect.top, containerRect.width / 2, containerRect.height),
          isEdge: true
        };
      }
      if (relY <= edgeThreshold) {
        return {
          targetZone: 'top-left' as DropZone,
          label: 'Place Top',
          description: 'Move component to the top',
          bounds: new DOMRect(containerRect.left, containerRect.top, containerRect.width, containerRect.height / 2),
          isEdge: true
        };
      }
      if (relY >= containerRect.height - edgeThreshold) {
        return {
          targetZone: 'bottom-left' as DropZone,
          label: 'Place Bottom',
          description: 'Move component to the bottom',
          bounds: new DOMRect(containerRect.left, containerRect.top + containerRect.height / 2, containerRect.width, containerRect.height / 2),
          isEdge: true
        };
      }
    }

    return null;
  }, [isActive, containerRef.current, draggedComponent, componentBounds, mousePosition, visibleComponentsByZone]);

  useEffect(() => {
    setCurrentDropZone(dropZoneInfo);
  }, [dropZoneInfo]);

  const handleDrop = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    if (currentDropZone && onDrop) {
      event.preventDefault();
      event.stopPropagation();

      const dragEvent = event as unknown as DragEvent;
      if (currentDropZone.isSwap && currentDropZone.swapWith) {
        (onDrop as unknown as (z: DropZone, e: DragEvent, s?: ComponentType) => void)(
          currentDropZone.targetZone, dragEvent, currentDropZone.swapWith
        );
      } else {
        onDrop(currentDropZone.targetZone, dragEvent);
      }
    }
  }, [currentDropZone, onDrop]);

  if (!isActive || !draggedComponent) {
    return null;
  }

  if (!currentDropZone) {
    return createPortal((
      <div className="fixed inset-0 z-50 pointer-events-none" style={{ background: 'rgba(0, 0, 0, 0.1)' }}>
        {componentBounds.map((component, index) => (
          <div
            key={`existing-${component.type}-${index}`}
            className="absolute border-2 border-border bg-muted/30 rounded-lg pointer-events-none"
            style={{
              left: component.rect.left,
              top: component.rect.top,
              width: component.rect.width,
              height: component.rect.height
            }}
          >
            <div className="flex items-center justify-center h-full">
              <div className="px-3 py-1 rounded-full text-sm font-medium bg-secondary text-secondary-foreground">
                {component.type}
              </div>
            </div>
          </div>
        ))}

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 pointer-events-none">
          <div className="bg-popover/95 text-popover-foreground px-6 py-3 rounded-lg shadow-xl border border-border">
            <div className="font-bold text-base">Moving {draggedComponent} component</div>
            <div className="text-sm text-muted-foreground mt-1">
              Drag to a valid drop zone
            </div>
          </div>
        </div>
      </div>
    ), document.body);
  }

  const overlayContent = (
    <div className="fixed inset-0 z-50 pointer-events-none" style={{ background: 'rgba(0, 0, 0, 0.1)' }}>
      {componentBounds.map((component, index) => (
        <div
          key={`existing-${component.type}-${index}`}
          className="absolute border-2 border-slate-300 bg-slate-100/30 rounded-lg pointer-events-none"
          style={{
            left: component.rect.left,
            top: component.rect.top,
            width: component.rect.width,
            height: component.rect.height
          }}
        >
          <div className="flex items-center justify-center h-full">
            <div className="px-3 py-1 rounded-full text-sm font-medium bg-slate-600 text-white">
              {component.type}
            </div>
          </div>
        </div>
      ))}

      <div
        className={cn(
          "absolute border-4 border-dashed rounded-lg",
          currentDropZone.isEdge
            ? "border-green-500 bg-green-500/15"
            : currentDropZone.isSwap
            ? "border-purple-500 bg-purple-500/15"
            : "border-blue-500 bg-blue-500/10"
        )}
        style={{
          left: currentDropZone.bounds.left,
          top: currentDropZone.bounds.top,
          width: currentDropZone.bounds.width,
          height: currentDropZone.bounds.height,
          pointerEvents: 'auto'
        }}
        onDrop={handleDrop}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer!.dropEffect = 'move';
        }}
        onDragEnter={(e) => e.preventDefault()}
      >
      </div>
    </div>
  );

  return createPortal(overlayContent, document.body);
};
